/**
 * Search Utilities Module
 *
 * Exports pure functions used for search text normalization and result highlighting.
 * This module is bundled with site.js by esbuild and can be imported directly for testing.
 */

/**
 * Character mappings for normalization. Mirrors PHP normalize_search_text(), which is also a
 * PURE character map — WP-slash removal happens at the PHP call sites, not in either normalizer —
 * so the client and server agree on matches character-for-character.
 */
export const NORMALIZE_MAP = {
    // Single quotes -> ASCII apostrophe
    '\u2018': "'",  // Left single quotation mark
    '\u2019': "'",  // Right single quotation mark
    // Double quotes -> ASCII double quote
    '\u201C': '"',  // Left double quotation mark
    '\u201D': '"',  // Right double quotation mark
    // Various dashes -> U+2010 Hyphen
    '-': '\u2010',      // Hyphen-Minus
    '\u2011': '\u2010', // Non-Breaking Hyphen
    '\u2012': '\u2010', // Figure Dash
    '\u2013': '\u2010', // En Dash
    '\u2014': '\u2010', // Em Dash
    '\u2015': '\u2010'  // Horizontal Bar
};

/**
 * Normalizes search text by replacing curly quotes with ASCII equivalents
 * and various dash characters with U+2010 hyphen.
 *
 * @param {string} text - The text to normalize
 * @returns {string} - The normalized text
 */
export function normalizeSearchText(text) {
    let result = '';
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        result += NORMALIZE_MAP[char] !== undefined ? NORMALIZE_MAP[char] : char;
    }
    return result;
}

/**
 * Lowercase a string while preserving its length 1:1. A few code points (e.g. U+0130 'İ') expand
 * under String.prototype.toLowerCase(), which would misalign match offsets taken against the
 * original string; for those we keep the original character (matched case-sensitively) rather than
 * shift every later index.
 *
 * @param {string} s
 * @returns {string}
 */
function lowerCasePreserveLength(s) {
    let result = '';
    for (let i = 0; i < s.length; i++) {
        const lower = s[i].toLowerCase();
        result += lower.length === 1 ? lower : s[i];
    }
    return result;
}

/**
 * Extracts settings from data attributes on a container element.
 *
 * @param {HTMLElement} el - The container element with data attributes
 * @returns {Object|null} - Settings object or null if required attributes are missing
 */
export function GetSettingsFromDataAttributes(el) {
    const restBaseUrl = el.dataset.restBaseUrl;
    const restSearchPath = el.dataset.restSearchPath;
    const parsedPostsPerPage = parseInt(el.dataset.postsPerPage, 10);
    const postsPerPage = (Number.isFinite(parsedPostsPerPage) && parsedPostsPerPage > 0) ? parsedPostsPerPage : 10;
    const postTypes = el.dataset.postTypes ? el.dataset.postTypes.split(',') : [];
    // A malformed data-post-type-labels JSON must not throw out of the init loop (which would
    // break this container and every one after it); fall back to no labels.
    let postTypeLabels = {};
    if (el.dataset.postTypeLabels) {
        try {
            postTypeLabels = JSON.parse(el.dataset.postTypeLabels);
        } catch (e) {
            if (typeof console !== 'undefined') console.error('ADF Search: invalid data-post-type-labels JSON', e);
        }
    }
    const instance = el.dataset.instance || '1';
    const showDateFilters = el.dataset.showDateFilters !== 'false';
    const showTypeFilters = el.dataset.showTypeFilters !== 'false';

    if (!restBaseUrl || !restSearchPath || postTypes.length === 0) {
        console.error('ADF Search: Missing required data attributes');
        return null;
    }

    return {
        restBaseUrl,
        restSearchPath,
        postsPerPage,
        postTypes,
        postTypeLabels,
        instance,
        showDateFilters,
        showTypeFilters
    };
}

/**
 * Creates an array of DOM element descriptors with marked (highlighted) search terms.
 * Handles overlapping term matches by merging them.
 *
 * @param {string} str - The string to search within
 * @param {string[]} terms - Array of search terms to highlight
 * @param {string} startingEllipsis - Optional ellipsis to prepend
 * @param {string} endingEllipsis - Optional ellipsis to append
 * @param {Function} BuildElement - The BuildElement function from adf-global-libs
 * @returns {Array} - Array of DOM elements (span and mark elements)
 */
export function GetMarkedElements(str, terms, startingEllipsis, endingEllipsis, BuildElement) {
    const elements = [];

    // Filter out empty/whitespace-only terms
    const validTerms = terms.filter(term => term.trim() !== '');

    if (startingEllipsis) elements.push(BuildElement("span", "", [], {}, startingEllipsis));

    if (validTerms.length === 0) {
        elements.push(BuildElement("span", "", [], {}, str));
    }
    else {
        // Find all match positions using normalized strings for comparison.
        // Normalization and length-preserving lowercasing are both 1:1, so positions map
        // directly back onto the original `str`.
        const matches = [];
        const normalizedLowerStr = lowerCasePreserveLength(normalizeSearchText(str));

        for (const term of validTerms) {
            const normalizedLowerTerm = lowerCasePreserveLength(normalizeSearchText(term));
            let pos = 0;
            while ((pos = normalizedLowerStr.indexOf(normalizedLowerTerm, pos)) !== -1) {
                matches.push({ start: pos, end: pos + normalizedLowerTerm.length });
                pos += normalizedLowerTerm.length;
            }
        }

        // Sort by start position and merge overlapping ranges
        matches.sort((a, b) => a.start - b.start);
        const merged = [];
        for (const m of matches) {
            if (merged.length === 0 || m.start > merged[merged.length - 1].end) {
                merged.push({ start: m.start, end: m.end });
            } else {
                merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, m.end);
            }
        }

        // Build elements from merged ranges
        let lastEnd = 0;
        for (const m of merged) {
            if (m.start > lastEnd) {
                elements.push(BuildElement("span", "", [], {}, str.substring(lastEnd, m.start)));
            }
            elements.push(BuildElement("mark", "", [], {}, str.substring(m.start, m.end)));
            lastEnd = m.end;
        }
        if (lastEnd < str.length) {
            elements.push(BuildElement("span", "", [], {}, str.substring(lastEnd)));
        }
    }

    if (endingEllipsis) elements.push(BuildElement("span", "", [], {}, endingEllipsis));

    return elements;
}

/**
 * Generates a summary excerpt with highlighted search terms.
 * Truncates long content and centers on the first matching term.
 *
 * @param {string} contentText - The full content text
 * @param {string[]} terms - Search terms for finding relevant excerpt location
 * @param {string[]} markTerms - Terms to highlight in the output
 * @param {number} characterCount - Maximum character count for the summary
 * @param {Function} BuildElement - The BuildElement function from adf-global-libs
 * @returns {Array} - Array of DOM elements for the summary
 */
export function GetSearchSummaryElements(contentText, terms, markTerms, characterCount, BuildElement) {
    let summary = contentText;
    const termCount = terms.length;
    let startingEllipsis = "";
    let endingEllipsis = "";

    if (contentText.length > characterCount) {
        const halfCount = Math.floor(characterCount / 2);
        startingEllipsis = "\u2026";
        endingEllipsis = "\u2026";

        let firstTermIndex = (termCount > 0) ? 9999 : 0;
        // Normalize + length-preserving lowercase both sides (matching the highlight pass), so a
        // curly-quote/dash term still finds its place in the normalized content and the excerpt
        // centers on the real match instead of defaulting to offset 0.
        const normalizedSummary = lowerCasePreserveLength(normalizeSearchText(summary));
        for (let t = 0; t < termCount; t++) {
            const termIndex = normalizedSummary.indexOf(lowerCasePreserveLength(normalizeSearchText(terms[t])));
            if ((termIndex !== -1) && (termIndex < firstTermIndex)) { firstTermIndex = termIndex; }
        }

        if ((firstTermIndex < halfCount) || (firstTermIndex === 9999)) {
            summary = summary.substr(0, characterCount);
            startingEllipsis = "";

        } else {
            summary = summary.substr(firstTermIndex - halfCount, characterCount);
        }

        // Now get all words, remove first and last in case they are partials. Only drop the
        // leading word when the excerpt was actually left-truncated (startingEllipsis set) —
        // otherwise a start-anchored excerpt would lose its genuine first word.
        const allWords = summary.replace(/(\r\n|\n|\r)/gm, " ").replace(/\s+/g, " ").trim().split(" ");
        if (startingEllipsis !== "") { allWords.shift(); }
        allWords.pop();
        summary = allWords.join(" ");
    }

    // Build elements with marked terms
    return GetMarkedElements(summary, markTerms, startingEllipsis, endingEllipsis, BuildElement);
}

/**
 * Derive up to 5 search terms from a URL path (used to prefill search on a 404 page).
 * Terms are collected from the LAST path segment backwards, so the most specific segment wins;
 * `_` and `-` are treated as word separators and a trailing `.php` is stripped.
 *
 * @param {string} pathname e.g. window.location.pathname
 * @returns {string} up to 5 space-separated terms (empty string when the path has none)
 */
export function GetTermsFromPath(pathname) {
    const pathSegments = (pathname || "")
        .replace(/\.php$/, "")
        .split("/")
        .filter(function (segment) { return segment.length > 0; });

    const segmentTerms = pathSegments.map(function (segment) {
        // Percent-decode so "/caf%C3%A9-rights" prefills "café rights"; keep the raw segment if
        // the escape sequence is malformed (decodeURIComponent would otherwise throw).
        try { segment = decodeURIComponent(segment); } catch (e) { /* keep raw segment */ }
        return segment
            .replace(/[_\-]/g, " ")
            .trim()
            .split(/\s+/)
            .filter(function (term) { return term.length > 0; });
    });

    let terms = [];
    for (let i = segmentTerms.length - 1; i >= 0 && terms.length < 5; i--) {
        terms = segmentTerms[i].concat(terms);
    }

    return terms.slice(0, 5).join(" ");
}
